import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from './customFetcher';
import {
    INotificationPreferences,
    notificationPreferencesSchema,
} from '../Interfaces/INotificationPreferences';

const PREFS_KEY = ['notificationPreferences'];

export const useNotificationPreferences = () => {
    const { data, isLoading } = useQuery<INotificationPreferences>({
        queryKey: PREFS_KEY,
        queryFn: async () => {
            const raw = await customFetcher<unknown>({ endpoint: '/notifications/preferences/' });
            return notificationPreferencesSchema.parse(raw);
        },
        staleTime: 60_000,
    });
    return { preferences: data ?? null, isLoading };
};

export const useUpdateNotificationPreferences = () => {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } = useMutation<
        INotificationPreferences,
        Error,
        Partial<INotificationPreferences>
    >({
        mutationFn: (patch) =>
            customFetcher<INotificationPreferences>({
                endpoint: '/notifications/preferences/',
                method: 'PATCH',
                body: patch,
            }),
        onSuccess: (data) => {
            queryClient.setQueryData(PREFS_KEY, data);
        },
    });
    return { updatePreferences: mutateAsync, isUpdating: isPending };
};

// ── Push subscription helpers ────────────────────────────────────────────────

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from(Array.from(raw).map((c) => c.charCodeAt(0)));
}

export async function getPushSupport(): Promise<boolean> {
    return 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
    if (!(await getPushSupport())) return null;
    const reg = await navigator.serviceWorker.ready;
    return reg.pushManager.getSubscription();
}

export async function subscribeToPush(): Promise<PushSubscription | null> {
    if (!(await getPushSupport())) return null;

    const keyRes = await customFetcher<{ publicKey: string }>({ endpoint: '/push/vapid-key/' });
    if (!keyRes.publicKey) return null;

    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.publicKey),
    });

    const json = sub.toJSON();
    await customFetcher({
        endpoint: '/push/subscribe/',
        method: 'POST',
        body: {
            endpoint: sub.endpoint,
            p256dh: json.keys?.p256dh ?? '',
            auth: json.keys?.auth ?? '',
        },
    });

    return sub;
}

export async function unsubscribeFromPush(): Promise<void> {
    const sub = await getCurrentPushSubscription();
    if (!sub) return;

    await customFetcher({
        endpoint: '/push/subscribe/',
        method: 'DELETE',
        body: { endpoint: sub.endpoint },
    });
    await sub.unsubscribe();
}
